import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { AREAS, PAGES, ROUTES } from "@/lib/menu";
import { fullMenu } from "@/hooks/useMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DotsSixVertical, Eye, EyeSlash, ArrowUp, ArrowDown, PencilSimple, Plus, ArrowCounterClockwise, FloppyDisk } from "@phosphor-icons/react";

const toConfig = (aree) => aree.map((a) => ({
  key: a.key, title: a.title, hidden: !!a.hidden,
  items: a.items.map((i) => ({ id: i.testid, label: i.label, to: i.to, hidden: !!i.hidden, custom: !!i.custom })),
}));

export default function MenuConfig() {
  const [aree, setAree] = useState([]);
  const [apertaKey, setApertaKey] = useState(null);
  const [edit, setEdit] = useState(null);
  const [nuova, setNuova] = useState(null);
  const [ripristina, setRipristina] = useState(false);
  const [drag, setDrag] = useState(null);
  const [filtro, setFiltro] = useState("non-collegate");
  const [aggiungi, setAggiungi] = useState(null);
  const [verifica, setVerifica] = useState(null);

  const statoPagine = useMemo(() => {
    const collegate = {};
    aree.forEach((a) => a.items.forEach((i) => {
      const st = a.hidden || i.hidden ? "NASCOSTA" : "COLLEGATA";
      if (!collegate[i.to] || st === "COLLEGATA") collegate[i.to] = { stato: st, area: a.title, id: i.id };
    }));
    return PAGES.map((p) => ({ ...p, areaKey: p.area, ...(collegate[p.to] || { stato: "NON COLLEGATA", area: null, id: "—" }) }));
  }, [aree]);

  const nonCollegate = statoPagine.filter((p) => p.stato === "NON COLLEGATA");
  const pagineFiltrate = statoPagine.filter((p) => (
    filtro === "tutte" ? true
      : filtro === "collegate" ? p.stato === "COLLEGATA"
        : filtro === "nascoste" ? p.stato === "NASCOSTA" : p.stato === "NON COLLEGATA"));

  const aggiungiAlMenu = () => {
    const nome = (aggiungi.label || "").trim();
    if (!nome || !aggiungi.areaKey) { toast.error("Indica area e nome visualizzato"); return; }
    const id = `nav-page-${aggiungi.to.replace(/[^a-z0-9]+/gi, "-")}`;
    setAree((p) => p.map((a) => {
      if (a.key !== aggiungi.areaKey) return a;
      const voce = { id, label: nome, to: aggiungi.to, hidden: false, custom: true };
      const items = aggiungi.pos === "prima" ? [voce, ...a.items] : [...a.items, voce];
      return { ...a, items };
    }));
    setAggiungi(null);
    toast.success("Voce aggiunta al menu: ricordati di salvare la configurazione");
  };

  const verificaMenu = () => {
    const ok = [];
    const avvisi = [];
    const visti = {};
    aree.forEach((a) => a.items.forEach((i) => {
      if (!i.to) { avvisi.push(`Voce senza destinazione: ${i.label} (${a.title})`); return; }
      if (!ROUTES.includes(i.to)) avvisi.push(`Collegamento a pagina inesistente: ${i.label} → ${i.to}`);
      else ok.push(`${i.label} → ${i.to}`);
      if (visti[i.to]) avvisi.push(`Voce duplicata: ${i.label} già presente in ${visti[i.to]}`);
      else visti[i.to] = a.title;
    }));
    nonCollegate.forEach((p) => avvisi.push(`Pagina esistente ma non collegata: ${p.label} (${p.to})`));
    setVerifica({ ok, avvisi });
  };

  const rotte = useMemo(() => {
    const out = [];
    AREAS.forEach((a) => a.items.forEach((i) => out.push(i.to)));
    return [...new Set(out)];
  }, []);

  useEffect(() => {
    api.get("/menu-config")
      .then((r) => setAree(toConfig(fullMenu(r.data.areas || []))))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const aperta = aree.find((a) => a.key === apertaKey);
  const tone = (key) => AREAS.find((a) => a.key === key)?.tone || {};

  const sposta = (lista, from, to) => {
    if (to < 0 || to >= lista.length) return lista;
    const copia = [...lista];
    copia.splice(to, 0, copia.splice(from, 1)[0]);
    return copia;
  };

  const spostaArea = (i, d) => setAree((p) => sposta(p, i, i + d));
  const spostaVoce = (i, d) => setAree((p) => p.map((a) => (a.key === apertaKey
    ? { ...a, items: sposta(a.items, i, i + d) } : a)));

  const toggleArea = (key) => setAree((p) => p.map((a) => (a.key === key ? { ...a, hidden: !a.hidden } : a)));
  const toggleVoce = (id) => setAree((p) => p.map((a) => (a.key === apertaKey
    ? { ...a, items: a.items.map((i) => (i.id === id ? { ...i, hidden: !i.hidden } : i)) } : a)));

  const salvaNome = () => {
    const nome = (edit.nome || "").trim();
    if (!nome) { toast.error("Il nome non può essere vuoto"); return; }
    setAree((p) => p.map((a) => {
      if (edit.tipo === "area") return a.key === edit.key ? { ...a, title: nome } : a;
      return a.key === edit.areaKey
        ? { ...a, items: a.items.map((i) => (i.id === edit.key ? { ...i, label: nome } : i)) } : a;
    }));
    setEdit(null);
  };

  const creaVoce = () => {
    const nome = (nuova.label || "").trim();
    if (!nome || !nuova.to) { toast.error("Indica nome e pagina collegata"); return; }
    const id = `nav-custom-${Date.now()}`;
    setAree((p) => p.map((a) => (a.key === nuova.areaKey
      ? { ...a, items: [...a.items, { id, label: nome, to: nuova.to, hidden: false, custom: true }] } : a)));
    setNuova(null);
  };

  const spostaInArea = (voceId, versoKey) => setAree((p) => {
    const voce = p.find((a) => a.key === apertaKey)?.items.find((i) => i.id === voceId);
    if (!voce || versoKey === apertaKey) return p;
    return p.map((a) => {
      if (a.key === apertaKey) return { ...a, items: a.items.filter((i) => i.id !== voceId) };
      if (a.key === versoKey) return { ...a, items: [...a.items, voce] };
      return a;
    });
  });

  const salva = async () => {
    try {
      await api.put("/menu-config", { areas: aree });
      toast.success("Configurazione del menu salvata");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const eseguiRipristino = async () => {
    try {
      await api.delete("/menu-config");
      const r = await api.get("/menu-config");
      setAree(toConfig(fullMenu(r.data.areas || [])));
      setRipristina(false);
      toast.success("Menu predefinito ripristinato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Riga = ({ i, nome, nascosta, tono, onSu, onGiu, onToggle, onRename, onApri, testid, dragKind, id }) => (
    <div draggable onDragStart={() => setDrag({ kind: dragKind, i })}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => {
        if (!drag || drag.kind !== dragKind) return;
        if (dragKind === "area") spostaArea(drag.i, i - drag.i); else spostaVoce(drag.i, i - drag.i);
        setDrag(null);
      }}
      data-testid={testid}
      className={`flex items-center gap-2 px-2 py-2 rounded-sm border font-mono text-[13px] ${
        nascosta ? "opacity-50 border-dashed border-border" : `${tono.sel || "border-border"}`}`}>
      <DotsSixVertical size={16} className="cursor-grab shrink-0 text-muted-foreground" />
      <span className={`w-6 text-right tabular-nums ${tono.text || ""}`}>{i + 1}</span>
      <button type="button" onClick={onApri} disabled={!onApri}
        className={`truncate uppercase text-left ${onApri ? "hover:underline" : ""}`} data-testid={`${testid}-open`}>
        {nome}
      </button>
      <div className="ml-auto flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Sposta su"
          onClick={onSu} data-testid={`${testid}-up`}><ArrowUp size={14} /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Sposta giù"
          onClick={onGiu} data-testid={`${testid}-down`}><ArrowDown size={14} /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title="Modifica nome"
          onClick={onRename} data-testid={`${testid}-rename`}><PencilSimple size={14} /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" title={nascosta ? "Mostra" : "Nascondi"}
          onClick={onToggle} data-testid={`${testid}-toggle`}>
          {nascosta ? <EyeSlash size={14} className="text-rose-600" /> : <Eye size={14} />}
        </Button>
        {dragKind === "item" && (
          <Select value="" onValueChange={(v) => spostaInArea(id, v)}>
            <SelectTrigger className="h-7 w-[130px] rounded-sm text-[11px]" data-testid={`${testid}-move-area`}>
              <SelectValue placeholder="Sposta in..." />
            </SelectTrigger>
            <SelectContent>
              {aree.filter((a) => a.key !== apertaKey).map((a) => (
                <SelectItem key={a.key} value={a.key}>{a.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="menu-config-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Configurazione Menu</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ordina, rinomina o nascondi le voci del menu. Le rotte, i permessi e le funzioni restano invariati.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={verificaMenu}
            data-testid="menu-verify-btn">Verifica menu</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setRipristina(true)}
            data-testid="menu-reset-btn"><ArrowCounterClockwise size={15} className="mr-1.5" /> Ripristina menu predefinito</Button>
          <Button size="sm" className="rounded-sm font-semibold" onClick={salva} data-testid="menu-save-btn">
            <FloppyDisk size={15} className="mr-1.5" /> Salva configurazione
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="rounded-sm border border-border bg-card p-3 space-y-2" data-testid="menu-config-list">
          {!aperta ? (
            <>
              <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Menu principale</div>
              {aree.map((a, i) => (
                <Riga key={a.key} i={i} id={a.key} nome={a.title} nascosta={a.hidden} tono={tone(a.key)}
                  dragKind="area" testid={`cfg-area-${a.key}`}
                  onSu={() => spostaArea(i, -1)} onGiu={() => spostaArea(i, 1)}
                  onToggle={() => toggleArea(a.key)}
                  onRename={() => setEdit({ tipo: "area", key: a.key, nome: a.title })}
                  onApri={() => setApertaKey(a.key)} />
              ))}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 rounded-sm" onClick={() => setApertaKey(null)}
                  data-testid="cfg-back">Indietro</Button>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{aperta.title}</div>
                <Button size="sm" className="h-7 rounded-sm ml-auto" data-testid="cfg-add-item"
                  onClick={() => setNuova({ areaKey: aperta.key, label: "", to: rotte[0] })}>
                  <Plus size={14} className="mr-1" /> Nuova voce
                </Button>
              </div>
              {aperta.items.map((it, i) => (
                <Riga key={it.id} i={i} id={it.id} nome={it.label} nascosta={it.hidden} tono={tone(aperta.key)}
                  dragKind="item" testid={`cfg-item-${it.id}`}
                  onSu={() => spostaVoce(i, -1)} onGiu={() => spostaVoce(i, 1)}
                  onToggle={() => toggleVoce(it.id)}
                  onRename={() => setEdit({ tipo: "item", areaKey: aperta.key, key: it.id, nome: it.label })} />
              ))}
            </>
          )}
        </div>

        <div className="rounded-sm border border-border bg-card p-3" data-testid="menu-config-preview">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Anteprima</div>
          <div className="rounded-sm border border-border p-2 font-mono text-[13px] space-y-1">
            <div className="text-center font-head font-bold uppercase py-2">
              {aperta ? aperta.title : "Menu Principale"}
            </div>
            {(aperta ? aperta.items.filter((i) => !i.hidden) : aree.filter((a) => !a.hidden)).map((x, i) => (
              <div key={x.key || x.id} className={`flex items-center gap-3 px-2 py-1.5 rounded-sm border ${
                i === 0 ? tone(aperta ? aperta.key : x.key).sel : "border-transparent"}`}>
                <span className={`w-5 text-right tabular-nums ${tone(aperta ? aperta.key : x.key).text}`}>{i + 1}</span>
                <span className={`h-4 w-0.5 rounded-sm ${tone(aperta ? aperta.key : x.key).bar}`} />
                <span className="truncate uppercase">{x.title || x.label}</span>
                <span className="ml-auto text-muted-foreground">&gt;</span>
              </div>
            ))}
            <div className="border-t border-border pt-2 text-[11px] text-muted-foreground">F1 = Guida · F3 = Uscita</div>
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 space-y-2" data-testid="pages-panel">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Pagine e funzioni disponibili
          </div>
          <span className={`text-[11px] font-semibold ${nonCollegate.length ? "text-amber-600" : "text-emerald-600"}`}
            data-testid="pages-unlinked-count">
            {nonCollegate.length
              ? `Pagine non collegate: ${nonCollegate.length}`
              : "✓ Tutte le pagine disponibili sono collegate al menu."}
          </span>
          <Select value={filtro} onValueChange={setFiltro}>
            <SelectTrigger className="h-8 w-[170px] rounded-sm ml-auto text-xs" data-testid="pages-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte</SelectItem>
              <SelectItem value="collegate">Collegate</SelectItem>
              <SelectItem value="non-collegate">Non collegate</SelectItem>
              <SelectItem value="nascoste">Nascoste</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-sm border border-border divide-y divide-border max-h-72 overflow-y-auto">
          {pagineFiltrate.map((p) => (
            <div key={p.to} className="flex flex-wrap items-center gap-2 px-3 py-2 font-mono text-[12px]"
              data-testid={`page-row-${p.to}`}>
              <span className={p.stato === "NON COLLEGATA" ? "text-amber-600" : p.stato === "NASCOSTA" ? "text-rose-600" : "text-emerald-600"}>
                {p.stato === "COLLEGATA" ? "✓" : "⚠"}
              </span>
              <span className="font-semibold uppercase truncate">{p.label}</span>
              <span className="text-muted-foreground">{p.to}</span>
              <span className="text-muted-foreground">· {p.id}</span>
              <span className="text-muted-foreground">· {p.area || AREAS.find((a) => a.key === p.areaKey)?.title || "—"}</span>
              <span className={`ml-auto text-[10px] font-bold ${p.stato === "COLLEGATA" ? "text-emerald-600" : p.stato === "NASCOSTA" ? "text-rose-600" : "text-amber-600"}`}>
                {p.stato}
              </span>
              {p.stato === "NON COLLEGATA" && (
                <Button size="sm" className="h-7 rounded-sm" data-testid={`page-add-${p.to}`}
                  onClick={() => setAggiungi({ to: p.to, label: p.label, areaKey: p.areaKey || aree[0]?.key, pos: "ultima" })}>
                  <Plus size={13} className="mr-1" /> Aggiungi al menu
                </Button>
              )}
            </div>
          ))}
          {pagineFiltrate.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="pages-empty">
              Nessuna pagina in questo filtro
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!aggiungi} onOpenChange={(o) => !o && setAggiungi(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="page-add-dialog">
          <DialogHeader><DialogTitle className="font-head">Aggiungi "{aggiungi?.label}" al menu</DialogTitle></DialogHeader>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Inserisci in</Label>
          <Select value={aggiungi?.areaKey || ""} onValueChange={(v) => setAggiungi({ ...aggiungi, areaKey: v })}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="page-add-area"><SelectValue placeholder="Area" /></SelectTrigger>
            <SelectContent>{aree.map((a) => <SelectItem key={a.key} value={a.key}>{a.title}</SelectItem>)}</SelectContent>
          </Select>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Posizione</Label>
          <Select value={aggiungi?.pos || "ultima"} onValueChange={(v) => setAggiungi({ ...aggiungi, pos: v })}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="page-add-pos"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="prima">Prima voce</SelectItem>
              <SelectItem value="ultima">Ultima voce</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nome visualizzato</Label>
          <Input value={aggiungi?.label || ""} onChange={(e) => setAggiungi({ ...aggiungi, label: e.target.value })}
            className="h-9 rounded-sm" data-testid="page-add-label" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setAggiungi(null)}>Annulla</Button>
            <Button className="rounded-sm font-semibold" onClick={aggiungiAlMenu} data-testid="page-add-save">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!verifica} onOpenChange={(o) => !o && setVerifica(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="menu-verify-dialog">
          <DialogHeader><DialogTitle className="font-head">Verifica menu</DialogTitle></DialogHeader>
          <div className="font-mono text-[12px] space-y-1">
            <div className="text-emerald-600 font-semibold">✓ {verifica?.ok.length || 0} voci correttamente collegate</div>
            {(verifica?.avvisi || []).map((a) => (
              <div key={a} className="text-amber-600">⚠ {a}</div>
            ))}
            {verifica && verifica.avvisi.length === 0 && (
              <div className="text-emerald-600">Nessuna anomalia rilevata nel menu</div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={() => setVerifica(null)} data-testid="menu-verify-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="menu-rename-dialog">
          <DialogHeader><DialogTitle className="font-head">Nome visualizzato</DialogTitle></DialogHeader>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nuovo nome</Label>
          <Input value={edit?.nome || ""} onChange={(e) => setEdit({ ...edit, nome: e.target.value })}
            className="h-9 rounded-sm" data-testid="menu-rename-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setEdit(null)}>Annulla</Button>
            <Button className="rounded-sm font-semibold" onClick={salvaNome} data-testid="menu-rename-save">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!nuova} onOpenChange={(o) => !o && setNuova(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="menu-new-dialog">
          <DialogHeader><DialogTitle className="font-head">Nuova voce di menu</DialogTitle></DialogHeader>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nome</Label>
          <Input value={nuova?.label || ""} onChange={(e) => setNuova({ ...nuova, label: e.target.value })}
            className="h-9 rounded-sm" data-testid="menu-new-label" />
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pagina collegata</Label>
          <Select value={nuova?.to || ""} onValueChange={(v) => setNuova({ ...nuova, to: v })}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="menu-new-route"><SelectValue placeholder="Seleziona pagina" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {rotte.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setNuova(null)}>Annulla</Button>
            <Button className="rounded-sm font-semibold" onClick={creaVoce} data-testid="menu-new-save">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ripristina} onOpenChange={setRipristina}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="menu-reset-dialog">
          <DialogHeader><DialogTitle className="font-head">Ripristinare la configurazione predefinita del menu?</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setRipristina(false)}>Annulla</Button>
            <Button className="rounded-sm font-semibold" onClick={eseguiRipristino} data-testid="menu-reset-confirm">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
